import { ThunkDispatch } from "@reduxjs/toolkit";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from "react-redux";

import {
  selectPanoramaAfterImageView,
  selectPanoramaAfterImageData,
  selectCanProcessPanorama,
  selectCanRegenPanorama,
  selectPanoramaSelectedBeforeImage,
} from '../../selectors';
import { panoramaActions, PanoramaBeforeImageState, PanoramaAfterImageState, uploadPanoramaImages } from '../../reducers/panorama';

import ExamplePhotoNG from '../ExamplePhotoNG';
import { ReloadRoomDesignIcon, GenerateAfterPhotoIcon, AddMorePhotoIcon, DelPhotoIcon } from '../SVGIcons';
import { PanoramaMaxRegenCount, PanoramaMaxUpload } from '../../constants';
import PanoramaViewer from './PanoramaViewer';

// State型定義
interface RootState {
  panorama: {
    isLoading: boolean;
    beforeImages: PanoramaBeforeImageState[];
    selectedBeforeImageStubId: string | null;
    afterImages: PanoramaAfterImageState[];
    selectedAfterImageId: number | null;
    runTime: number;
    maxExecTime: number;
    regenerationCount: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const afterImageView = selectPanoramaAfterImageView(state);
  const selectedAfterImageData = selectPanoramaAfterImageData(state);
  const canProcess = selectCanProcessPanorama(state);
  const canRegen = selectCanRegenPanorama(state);
  const selectedBeforeImage = selectPanoramaSelectedBeforeImage(state);

  return {
    ...state.panorama,
    afterImageView,
    selectedAfterImageData,
    canProcess,
    canRegen,
    selectedBeforeImage,
  };
}

// コンポーネントのProps型定義
interface PanoramaEditFormSomeProps {
  isLoading: boolean;
  beforeImages: PanoramaBeforeImageState[];
  selectedBeforeImageStubId: string | null;
  selectedBeforeImage: PanoramaBeforeImageState | null;
  afterImages: PanoramaAfterImageState[];
  selectedAfterImageId: number | null;
  runTime: number;
  maxExecTime: number;
  regenerationCount: number;
  afterImageView: string;
  selectedAfterImageData: PanoramaAfterImageState | null;
  canProcess: boolean;
  canRegen: boolean;
  generatingImages: Record<string, { current: number; total: number }>;
}

const PanoramaEditFormSome = (props: PanoramaEditFormSomeProps) => {
  const {
    isLoading,
    beforeImages,
    selectedBeforeImageStubId,
    selectedBeforeImage,
    afterImages,
    selectedAfterImageId,
    runTime,
    maxExecTime,
    regenerationCount,
    afterImageView,
    selectedAfterImageData,
    canProcess,
    canRegen,
    generatingImages,
  } = props;

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const inputFile = useRef<HTMLInputElement>(null);
  // パノラマビューワーのローディング状態
  const [isViewerLoading, setIsViewerLoading] = useState(false);

  // fileUrlがない場合はローディング状態をリセット
  useEffect(() => {
    if (!selectedAfterImageData?.fileUrl) {
      setIsViewerLoading(false);
    }
  }, [selectedAfterImageData?.fileUrl]);

  // 画像追加可能かどうか（最大5枚、ローディング中でない、処理済み画像がない）
  const hasProcessedImages = beforeImages.some(img => img.id);
  const canAddFile = beforeImages.length < PanoramaMaxUpload && !isLoading && !hasProcessedImages;

  // プログレス表示用
  const processPercent = runTime < maxExecTime * 0.9 ? runTime / maxExecTime : 0.9;
  const displayPercent = Math.floor(processPercent * 100) + "%";
  const strokeDashoffset = 100 - 100 * processPercent;

  // 処理済みかどうか
  const isProcessed = selectedBeforeImage && selectedBeforeImage.id;
  // 選択中のbeforeImageのafterImagesを使用
  const currentAfterImages = selectedBeforeImage?.afterImages || [];
  const hasAfterImages = currentAfterImages.length > 0;
  const remainingRegenCount = PanoramaMaxRegenCount - (selectedBeforeImage?.regenerationCount || 0);
  // 未処理の画像数
  const unprocessedImageCount = beforeImages.filter(img => !img.id).length;
  const hasUnprocessedImages = unprocessedImageCount > 0;
  const canExecute = canProcess && !isLoading && hasUnprocessedImages;

  // before画像選択
  const handleSelectBeforeImage = useCallback((stubId: string) => {
    // 生成完了状態の場合は進捗表示をクリア
    const progress = generatingImages[stubId];
    if (progress && progress.current === progress.total) {
      dispatch(panoramaActions.clearGenerationProgress(stubId));
    }

    const selectedImage = beforeImages.find(img => img.stubId === stubId);
    dispatch(panoramaActions.setSelectedBeforeImageStubId(stubId));
    // 選択した画像のafterImagesを表示
    if (selectedImage) {
      dispatch(panoramaActions.setAfterImages(selectedImage.afterImages || []));
      dispatch(panoramaActions.setSelectedAfterImageId(selectedImage.selectedAfterImageId || null));
    }
  }, [dispatch, beforeImages, generatingImages]);

  // after画像選択
  const setSelectedAfterImage = useCallback((id: number) => {
    dispatch(panoramaActions.setSelectedAfterImageId(id));
  }, [dispatch]);

  // 一括削除確認モーダルを表示
  const handleClearAllClick = () => {
    dispatch(panoramaActions.setIsClearAllPopupShow(true));
  };

  // 実行確認ポップアップを表示
  const handleProcessClick = () => {
    if (!canProcess) return;
    dispatch(panoramaActions.setIsConfirmPopupShow(true));
  };

  // 再生成確認ポップアップを表示
  const handleRegenClick = () => {
    if (!canRegen) return;
    dispatch(panoramaActions.setIsRegenPopupShow(true));
  };

  // 新規画像を実行
  const handleNewImageClick = () => {
    dispatch(panoramaActions.setIsClearAllPopupShow(true));
  };

  // 画像追加ボタンクリック
  const onButtonAddImage = () => {
    inputFile.current?.click();
  };

  // 画像追加処理
  const addBeforeImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      dispatch(uploadPanoramaImages(files));
    }
    // inputをリセット（同じファイルを再選択可能にする）
    if (inputFile.current) {
      inputFile.current.value = '';
    }
  };

  return (
    <>
      <div className="max-w-8xl w-full mx-auto pt-1 pb-3 px-4 sm:px-6 md:px-8 text-[#2d2d2d]">
        <div className="grid grid-cols-9 gap-2 items-stretch">
          {/* 左サイドバー */}
          <div className="col-1 bg-gray-200 overflow-y-auto rounded min-h-[36.5rem] flex flex-col p-2">
            <button
              type="button"
              onClick={handleClearAllClick}
              disabled={isLoading}
              className={!isLoading
                ? "bg-gray-50 flex p-2 rounded-md border border-yellow-50 mb-4 text-sm text-yellow-700 items-center justify-center"
                : "bg-gray-300 flex p-2 rounded-md mb-4 text-sm items-center justify-center"
              }
            >
              <ReloadRoomDesignIcon />一括削除
            </button>
            <ul className="space-y-2">
              {beforeImages.map((image) => {
                const progress = generatingImages[image.stubId];
                // 進捗オーバーレイを表示するか
                const showProgress = progress !== undefined;
                // 生成中かどうか（UIの表示用）
                const isInProgress = progress !== undefined && progress.current < progress.total;

                return (
                  <li
                    key={image.stubId}
                    onClick={() => !isViewerLoading && handleSelectBeforeImage(image.stubId)}
                    className={`flex items-center justify-center w-full bg-gray-300 h-24 w-28 overflow-hidden mx-auto rounded-sm relative ${
                      isViewerLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    } ${
                      selectedBeforeImageStubId === image.stubId ? 'ring-offset-2 ring-2 ring-blue-500' : ''
                    }`}
                  >
                    {/* 処理済バッジ（進捗オーバーレイがない場合のみ表示） */}
                    {image.id && !showProgress && (
                      <span className="bg-[#ff4600cc] absolute top-1 left-1 text-xs text-white rounded-lg px-1 z-10">処理済</span>
                    )}
                    {/* 処理済みでない場合のみ削除ボタンを表示 */}
                    {!isLoading && !image.id && !showProgress && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(panoramaActions.removeBeforeImage(image.stubId));
                        }}
                        className="absolute top-1 right-1 opacity-50 hover:opacity-70 z-10"
                        title="画像を削除"
                      >
                        <DelPhotoIcon className="w-4 h-4" />
                      </button>
                    )}
                    {/* 生成中/完了オーバーレイ */}
                    {showProgress && (
                      <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-20 pointer-events-none">
                        {progress.current === progress.total ? (
                          <span className="bg-[#ff4600cc] text-white text-sm font-bold rounded-lg px-3 py-1.5">生成完了</span>
                        ) : (
                          <div className="bg-[#ff4600cc] rounded-lg px-3 py-2">
                            <p className="text-white text-xs font-bold text-center mb-0.5">生成中</p>
                            <p className="text-white text-sm font-bold text-center">{progress.current}/{progress.total}枚</p>
                          </div>
                        )}
                      </div>
                    )}
                    <img
                      src={image.uri}
                      className="block w-full"
                      alt=""
                    />
                  </li>
                );
              })}
              {/* 画像追加ボタン */}
              {canAddFile && (
                <li className="text-center">
                  <label>
                    <button
                      type="button"
                      onClick={onButtonAddImage}
                      className="text-blue-600 hover:text-blue-800 rounded-full hover:bg-gray-100 p-2"
                    >
                      <AddMorePhotoIcon />
                    </button>
                    <input
                      type="file"
                      ref={inputFile}
                      multiple
                      accept="image/png, image/jpeg, image/webp"
                      onChange={addBeforeImages}
                      className="hidden"
                    />
                  </label>
                </li>
              )}
            </ul>
          </div>

          {/* メインコンテンツ（画像エリアのみ） */}
          <div
            className="col-span-8 border border-gray-300 bg-white rounded grid grid-cols-2"
            style={{ height: '584px' }}
          >
            {/* 左側: アップロードされた画像 */}
            <div className="h-full overflow-hidden p-3 flex flex-col">
              {/* 右パネルの数字ボタン分のスペーサー */}
              <div style={{ height: '32px', flexShrink: 0 }}></div>
              <div className="flex items-start justify-center overflow-hidden" style={{ height: '504px' }}>
                {selectedBeforeImage && (
                  <img
                    src={selectedBeforeImage.uri}
                    style={{
                      maxHeight: '504px',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                      borderRadius: '0.25rem'
                    }}
                    alt=""
                  />
                )}
              </div>
            </div>

            {/* 右側: 結果表示エリア */}
            <div className="border-l border-gray-300 h-full overflow-hidden flex flex-col">
                {afterImageView === 'show_error' && (
                  <div className="p-3 h-full flex">
                    <div className="relative m-auto text-red-600 text-center text-lg">
                      {selectedBeforeImage?.errorMessage || "画像処理に失敗しました。"}<br />
                      再度処理を行ってください。<br />
                      ※クレジットは消費されておりません。
                    </div>
                  </div>
                )}

                {afterImageView === 'show_loading' && (
                  <div className="p-3 h-full flex">
                    <div className="relative size-40 m-auto">
                      <svg className="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-gray-200" strokeWidth="2" />
                        <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-blue-600" strokeWidth="2" strokeDasharray="100" strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
                      </svg>
                      <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
                        <span className="text-center text-2xl font-bold text-blue-600">{displayPercent}</span>
                      </div>
                    </div>
                  </div>
                )}

                {afterImageView === 'show_blank' && (
                  <ExamplePhotoNG tabName="itemRemoval" />
                )}

                {afterImageView === 'show_images' && (
                  <div className="relative w-full flex-1 flex flex-col p-3 overflow-hidden">
                    {/* 画像選択ボタン */}
                    {currentAfterImages.length > 0 && (
                      <div className="flex mb-2">
                        <div className="grid grid-cols-9 gap-1">
                          {currentAfterImages.map((image: PanoramaAfterImageState, index: number) => (
                            <button
                              key={image.id}
                              onClick={() => setSelectedAfterImage(image.id)}
                              disabled={isViewerLoading}
                              className={`min-w-6 min-h-6 rounded ${
                                isViewerLoading
                                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                                  : selectedAfterImageId === image.id
                                    ? "submit-btn text-white hover:text-gray-400"
                                    : "bg-gray-800 text-white hover:text-gray-400"
                              }`}
                            >
                              {index + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* パノラマビューワー */}
                    <div className="overflow-hidden" style={{ height: '504px' }}>
                      {selectedAfterImageData && selectedAfterImageData.fileUrl ? (
                        <PanoramaViewer
                          key={selectedAfterImageData.id}
                          imageUrl={selectedAfterImageData.fileUrl}
                          height="504px"
                          onLoadingChange={setIsViewerLoading}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center bg-gray-100">
                          <span className="text-gray-500">画像を読み込めませんでした</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* ボタンエリア（画像エリアの外側） */}
        <div className="grid grid-cols-9 gap-2 mt-2">
          {/* 左サイドバーの下は空 */}
          <div className="col-1"></div>
          {/* ボタン配置エリア */}
          <div className="grid grid-cols-2 col-span-8 gap-0">
            {/* 左側: 実行ボタン（センタリング） */}
            <div className="flex justify-center">
              <button
                className={`px-5 py-2.5 flex items-center gap-1 rounded-full text-sm ${
                  canExecute
                    ? "text-white submit-btn hover:opacity-80"
                    : "text-gray-900 bg-gray-400 disabled:opacity-75"
                }`}
                disabled={!canExecute}
                type="button"
                onClick={handleProcessClick}
              >
                <GenerateAfterPhotoIcon />
                パノラマステージングを実行
                <span className="inline-flex items-center justify-center w-4 h-4 ms-2 text-xs font-semibold text-blue-800 submit-subtext rounded-full">
                  {unprocessedImageCount}
                </span>
              </button>
            </div>
            {/* 右側: 再生成・新規画像ボタン（2等分） */}
            <div className="grid grid-cols-2 gap-2">
              {isProcessed && hasAfterImages ? (
                <>
                  <button
                    className={`py-2.5 flex items-center justify-center rounded-full text-sm ${
                      canRegen && !isLoading
                        ? "submit-btn-2nd hover:bg-gray-100"
                        : "text-gray-900 bg-gray-400 disabled:opacity-75"
                    }`}
                    disabled={!canRegen || isLoading}
                    type="button"
                    onClick={handleRegenClick}
                  >
                    <ReloadRoomDesignIcon />
                    {remainingRegenCount === PanoramaMaxRegenCount
                      ? "再生成"
                      : `再生成（残り${remainingRegenCount}回）`
                    }
                  </button>
                  <button
                    className={`py-2.5 flex items-center justify-center rounded-full text-sm ${
                      !isLoading
                        ? "text-white submit-btn hover:opacity-80"
                        : "text-gray-900 bg-gray-400 disabled:opacity-75"
                    }`}
                    disabled={isLoading}
                    type="button"
                    onClick={handleNewImageClick}
                  >
                    <GenerateAfterPhotoIcon />
                    新規画像を実行
                  </button>
                </>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(mapStateToProps)(PanoramaEditFormSome);
